import React from 'react';
import { AbsoluteFill } from 'remotion';
import { TransitionSeries, linearTiming } from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { none } from '@remotion/transitions/none';
import { CompositorComposition } from './CompositorComposition';
import { CompositorManifest } from '../types/manifest';
import { TransitionType } from '../../../../types/scene';

export interface ScenePlaybackItem {
  sceneId: string;
  sceneTitle: string;
  manifest: CompositorManifest;
  durationFrames: number;
  transitionOut: {
    type: TransitionType;
    durationFrames: number;
  };
}

interface MultiSceneCompositionProps {
  scenes: ScenePlaybackItem[];
  canvasWidth: number;
  canvasHeight: number;
}

function getRemotionPresentation(type: TransitionType) {
  switch (type) {
    case 'cut':
      return none();
    case 'fade':
      return fade({ shouldFadeOutExitingScene: true });
    case 'crossfade':
      return fade({ shouldFadeOutExitingScene: true });
    case 'slide':
      return slide({ direction: 'from-right' });
    default:
      return none();
  }
}

const SceneWrapper: React.FC<{ manifest: CompositorManifest; sceneTitle: string }> = React.memo(({ manifest, sceneTitle }) => {
  return <CompositorComposition manifest={manifest} />;
});

const PlaceholderScene: React.FC<{ 
  text: string;
  canvasWidth: number;
  canvasHeight: number;
}> = ({ text, canvasWidth, canvasHeight }) => (
  <AbsoluteFill
    style={{
      width: canvasWidth,
      height: canvasHeight,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div
      style={{
        color: 'white',
        fontSize: 32,
        fontWeight: 600,
        textShadow: '0 2px 8px rgba(0,0,0,0.3)',
        textAlign: 'center',
        padding: 40,
      }}
    >
      {text}
    </div>
  </AbsoluteFill>
);

export const MultiSceneComposition: React.FC<MultiSceneCompositionProps> = React.memo(({
  scenes,
  canvasWidth,
  canvasHeight,
}) => {

  if (scenes.length === 0) {
    return (
      <AbsoluteFill style={{ backgroundColor: '#1a1a1a' }}>
        <PlaceholderScene text="No scenes to play" canvasWidth={canvasWidth} canvasHeight={canvasHeight} />
      </AbsoluteFill>
    );
  }

  if (scenes.length === 1) {
    const scene = scenes[0];
    return (
      <AbsoluteFill
        style={{
          width: canvasWidth,
          height: canvasHeight,
          overflow: 'hidden',
          backgroundColor: '#000',
        }}
      >
        <SceneWrapper manifest={scene.manifest} sceneTitle={scene.sceneTitle} />
      </AbsoluteFill>
    );
  }

  return (
    <AbsoluteFill
      style={{
        width: canvasWidth,
        height: canvasHeight,
        overflow: 'hidden',
        backgroundColor: '#000',
      }}
    >
      <TransitionSeries>
        {scenes.map((scene, index) => {
          const isLastScene = index === scenes.length - 1;
          const transition = scene.transitionOut;
          const presentation = getRemotionPresentation(transition.type);
          const effectiveTransitionFrames = transition.type === 'cut' ? 1 : transition.durationFrames;
          const timing = linearTiming({ durationInFrames: effectiveTransitionFrames });

          return (
            <React.Fragment key={scene.sceneId}>
              <TransitionSeries.Sequence durationInFrames={Math.max(1, scene.durationFrames)}>
                {scene.manifest ? (
                  <SceneWrapper manifest={scene.manifest} sceneTitle={scene.sceneTitle} />
                ) : (
                  <PlaceholderScene
                    text={`${scene.sceneTitle} (No Data)`}
                    canvasWidth={canvasWidth}
                    canvasHeight={canvasHeight}
                  />
                )}
              </TransitionSeries.Sequence>

              {!isLastScene && (
                <TransitionSeries.Transition
                  presentation={presentation}
                  timing={timing}
                />
              )}
            </React.Fragment>
          );
        })}
      </TransitionSeries>
    </AbsoluteFill>
  );
});

export default MultiSceneComposition;
